import { QuestionCircleOutlined } from '@ant-design/icons';
import { useLocalStorageState } from 'ahooks';
import { Form, Tooltip } from 'antd';
import { isPlainObject, unset } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { FC, useEffect, useRef, useState, useMemo } from 'react';
import { useAppConfig } from '@/config/context';
import { toSearchObj, getParamFromUrl } from '@/infrastructure/history';
import { getProductId, isAndroid, isFlutter, isIOS, isMac, isVisionOS } from '@/infrastructure/product-info';
import { openXmMeter, openXmMeterOpenCes } from '@/infrastructure/reporter/xmMeter/index';
import { getCompareDateValue, isDataLabelingPlatform, judgmentTime } from '@/infrastructure/utils/issue';
import { CommonDateRange, DATE_TIME_FORMAT, getDateRangeByShortCut } from '@/infrastructure/utils/timestamp';
import {
  getAppInBack,
  getExceptionTypeOptions,
  getTableTimeQuickOptions,
  getTimeQuickOptions, TABLE_DEVICE_TYPE_OPTIONS,
} from '@/presentation/components/exception-form/utils';
import { FormEntry, FormType, IFormRef, QueryForm } from '@/presentation/components/form';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { shortRanges, CUSTOM } from '@/presentation/components/form/components/time-range-cascade';
import { FieldsOperator, TIME_TYPE_OPTIONS_BUGLY } from '@/presentation/components/form/proto';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS, INFERRED_LABELS_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS, SPECIFIC_TYPE,
  STACK_CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import { ContinuousEventTips } from '@/presentation/components/issue-list/components/continuous-event-tips';
import {
  attachFileNamesText,
  cpuArchitectureText,
  cpuArchText,
  enumFilterTips,
  numberFilterTips,
  specialFilterTips,
  stringArrayFilterTips,
  versionFilterTips,
  asanTypeTips, DATA_LABELING_TIPS,
} from '@/presentation/components/issue-list/components/tips';
import useToggle from '@/presentation/components/toggle/use-toggle';
import { SEARCH_VALUES, TIME_RANGE } from '@/presentation/config/storage';
import { SearchValueStorageType, TimeRangeStorageType } from '@/presentation/config/type';
import { getIssueFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { useStores } from '@/presentation/store';
import {
  ANR_SCENE_DATA_LABELS_OPTIONS,
  CRASH_SCENE_DATA_LABELS_OPTIONS,
  ERROR_FOOM_SCENE_DATA_LABELS_OPTIONS, getMonitorType,
  INIT_FIELD_OPTIONS,
  IssueFilterOptions,
} from '@/presentation/store/issue';
import {
  ANDROID_ANR_LINKAGES_OPTION,
  ANDROID_ATTACH_FILE_OPTIONS,
  ANDROID_CRASH_LINKAGES_OPTION,
  ExOption,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  getLocalField,
  IOS_ATTACH_FILE_OPTIONS,
  Zone,
  ASAN_TYPE_OPTION,
  ANDROID_FOOM_LINKAGES_OPTION,
} from '@/proto/field';
import { getFieldZone, IssueType } from '@/proto/issue';
import { MetricType, TimeType } from '@/proto/metric';
import { tokenizeValue } from '../form/utils';
// import styles from './index.module.scss';

export interface IProps {
  issueType: IssueType;
  metricType: MetricType;
  entry: FilterEntry;
  onFinish: (value: Record<string, any>) => void;
  initialValue?: any;
  onDeletedKeys?: (keys: string[]) => void;
  defaultValues?: any;
}

const dateRangeTransformMapping: Record<string, string | string[]> = {
  last_1_hour: '近1小时',
  last_1_day: '近1天',
  last_3_day: '近3天',
  last_7_day: '近7天',
  last_15_day: '近15天',
  last_30_day: '近30天',
};

export const getUrlFilterParams = () => {
  const query: Record<string, string | string[]> = toSearchObj(window.location.search);
  unset(query, 'productId');
  unset(query, 'pid');
  unset(query, 'appId');
  unset(query, 'token');

  Object.keys(query).forEach((queryKey: string) => {
    if (!query[queryKey] || query[queryKey] === 'all') {
      unset(query, queryKey);
    }
  });

  if (query.newTagList) {
    query.newTagList = [query.newTagList as string];
  }

  if (query.date && query.date !== 'custom') {
    query.customDate = dateRangeTransformMapping[query.date as string] as string;
  } else if (query.date && query.date === 'custom') {
    query.startDateStr = moment(query.startDateStr).format('YYYY-MM-DD 00:00:00');
    query.endDateStr = moment(query.endDateStr).format('YYYY-MM-DD 23:59:59');
    query.timeRange = [
      query.startDateStr,
      query.endDateStr,
    ];
  }

  return query;
};

export const ExceptionForm: FC<IProps> = observer((props) => {
  const { entry, metricType, issueType } = props;
  const [form] = Form.useForm();
  const [timeRangeState, setTimeRangeState] = useLocalStorageState<TimeRangeStorageType>(TIME_RANGE, {});
  const tableRef = useRef<IFormRef>(null);
  const [options, setOptions] = useState<IssueFilterOptions>(INIT_FIELD_OPTIONS);
  const [showDeviceType] = useToggle('deviceType', ['appId']);
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const { getOptions } = useStores().IssueStore;
  const getDefault = (name: string) => options[name]?.options?.[0]?.value;
  const exceptionTypeList = getExceptionTypeOptions(props.issueType) || [];
  const [mainStackEnabled] = useToggle('enableMainStack', ['appId']);
  const config = useAppConfig();
  const { filterConfig } = useStores().ProductStore;
  const inferredLabelsConfig = getIssueFilterConfig(issueType, filterConfig);
  const isErrorList = entry === FilterEntry.ErrorList;
  const isBuglyListPage = [
    FilterEntry.ANRList,
    FilterEntry.CrashList,
    FilterEntry.ErrorList,
    FilterEntry.FoomList,
  ].includes(entry);
  const isBuglyDetailPage = [
    FilterEntry.ANRDetail,
    FilterEntry.CrashDetail,
    FilterEntry.ErrorDetail,
    FilterEntry.FoomDetail,
    FilterEntry.ANRAnalysis,
    FilterEntry.CrashAnalysis,
    FilterEntry.ErrorAnalysis,
    FilterEntry.FoomAnalysis,
  ].includes(entry);

  const isBuglyCaseDetailPage = [
    FilterEntry.ANRDetail,
    FilterEntry.CrashDetail,
    FilterEntry.ErrorDetail,
    FilterEntry.FoomDetail,
    FilterEntry.CrashCaller,
    FilterEntry.AndroidANRCaller,
  ].includes(entry);

  const hintText = (label: string, tips: React.ReactNode) => <Tooltip
    title={tips}
    overlayStyle={{ maxWidth: 800 }}
  >
    {label} <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }} />
  </Tooltip>;

  useEffect(() => {
    const load = async () => {
      const options = await getOptions(props.metricType);
      setOptions(options);
    };

    load();
  }, [props.metricType]);


  const judgmentTimeCallback = () => {
    form.setFieldsValue({
      timeQuick: CUSTOM,
      timeWindow: TimeType.DAY,
      timeRange: CommonDateRange.lastDaysFull(90),
    });
    form.submit();
  };

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    // 对时间值做动态更新
    if (value.timeQuick && value.timeQuick !== 'custom') {
      const timeRange = shortRanges?.[value.timeQuick!]?.() ?? [];
      if (timeRange[0] && timeRange[1]) {
        // eslint-disable-next-line no-param-reassign
        value.timeRange = [moment(timeRange[0]).format('YYYY-MM-DD HH:mm:ss'), moment(timeRange[1]).format('YYYY-MM-DD HH:mm:ss')];
      }
    }

    // 对比模式
    if (value.timeQuickCompare && value.timeQuickCompare !== 'custom') {
      const timeRange = shortRanges?.[value.timeQuickCompare!]?.() ?? [];
      if (timeRange[0] && timeRange[1]) {
        // eslint-disable-next-line no-param-reassign
        value.timeRangeCompare = [moment(timeRange[0]).format('YYYY-MM-DD HH:mm:ss'), moment(timeRange[1]).format('YYYY-MM-DD HH:mm:ss')];
      }
    }

    let expired = false;
    if (Array.isArray(value.timeRange) && value.timeRange.length === 2) {
      expired = judgmentTime(
        moment(value.timeRange[0]),
        moment(value.timeRange[1]),
        isBuglyListPage ? value.timeWindow : undefined,
        false,
        true,
        judgmentTimeCallback,
      );
    }
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    !expired && props.onFinish?.({ ...value, ...compareDateValue });

    // ces满意度弹窗
    if (compareSwitch) {
      openXmMeter('8a3e1wev7j6dy2lvmuco');
    }
    // 缓存时间
    if (value.timeWindow && value.timeQuick) {
      setTimeRangeState(state => ({
        ...state,
        [entry]: {
          timeWindow: value.timeWindow,
          timeQuick: value.timeQuick,
        },
      }));
    }
  };

  const isApple = isIOS() || isMac() || isVisionOS();


  const searchValueDeserializer = (value?: SearchValueStorageType) => value?.map?.((item) => {
    const dateRange = getDateRangeByShortCut(item?.values?.timeQuick ?? '', shortRanges);
    if (!Array.isArray(dateRange) || dateRange?.length !== 2) {
      return item;
    }
    const compareDate: Record<string, any> = {};
    if (item?.values?.isCompareOpen) {
      const dateRangeCompare = getDateRangeByShortCut(item?.values?.timeQuickCompare ?? '', shortRanges);
      compareDate.startDateStrCompare = moment(dateRangeCompare[0]).format(DATE_TIME_FORMAT);
      compareDate.endDateStrCompare = moment(dateRangeCompare[1]).format(DATE_TIME_FORMAT);
    }
    const startDateStr = moment(dateRange[0]).format(DATE_TIME_FORMAT);
    const endDateStr = moment(dateRange[1]).format(DATE_TIME_FORMAT);
    return { name: item.name, id: item.id, values: { ...item?.values, startDateStr, endDateStr, ...compareDate } };
  }) || [];

  const getSceneDataLabelsOptions = () => {
    if ([FilterEntry.CrashDetail, FilterEntry.CrashCaller].includes(entry)) {
      return CRASH_SCENE_DATA_LABELS_OPTIONS;
    }
    if ([FilterEntry.ANRDetail, FilterEntry.AndroidANRCaller].includes(entry)) {
      return ANR_SCENE_DATA_LABELS_OPTIONS;
    }
    return ERROR_FOOM_SCENE_DATA_LABELS_OPTIONS;
  };

  const getLinkageOptions = () => {
    if (!isAndroid()) {
      return [];
    }
    switch (entry) {
      case FilterEntry.CrashList:
      case FilterEntry.CrashDetail:
      case FilterEntry.CrashCaller:
        return ANDROID_CRASH_LINKAGES_OPTION;
      case FilterEntry.ANRList:
      case FilterEntry.ANRDetail:
      case FilterEntry.AndroidANRCaller:
        return ANDROID_ANR_LINKAGES_OPTION;
      default:
        return [];
    }
  };

  const getAsanOptions = () => ASAN_TYPE_OPTION;

  const isLinkageDisabled = ![
    FilterEntry.CrashList,
    FilterEntry.CrashDetail,
    FilterEntry.CrashCaller,
    FilterEntry.ANRList,
    FilterEntry.ANRDetail,
    FilterEntry.AndroidANRCaller,
  ].includes(entry);

  // 是否展示小满费力度调查
  const isCesDisabled = [
    FilterEntry.CrashDetail,
    FilterEntry.CrashAnalysis,
  ].includes(entry);

  const getDefaultTime = useMemo(() => {
    const timeRangeStateData = timeRangeState?.[entry];
    // 有时间缓存则使用时间缓存，没有则使用默认时间
    if (timeRangeStateData?.timeQuick && timeRangeStateData.timeWindow) {
      let dateRange = getDateRangeByShortCut(timeRangeStateData?.timeQuick ?? '', shortRanges);
      if (timeRangeStateData?.timeQuick === CUSTOM
        && [TimeType.MINUTE5, TimeType.HOUR].includes(timeRangeStateData?.timeWindow)) {
        dateRange = getDateRangeByShortCut('今天', shortRanges);
      }
      if (timeRangeStateData?.timeQuick === CUSTOM
        && TimeType.WEEK === timeRangeStateData?.timeWindow) {
        dateRange = getDateRangeByShortCut('近30天', shortRanges);
      }
      if (timeRangeStateData?.timeQuick === CUSTOM
        && TimeType.MONTH === timeRangeStateData?.timeWindow) {
        dateRange = getDateRangeByShortCut('近90天', shortRanges);
      }
      return { timeQuick: timeRangeStateData?.timeQuick,
        timeWindow: timeRangeStateData?.timeWindow,
        initRange: dateRange };
    }
    return { timeWindow: TimeType.MINUTE5,
      timeQuick: 'custom',
      initRange: CommonDateRange.today() };
  }, [timeRangeState]);


  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      label: FIELD_LABEL[FieldKey.TimeRange],
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      metricType: metricType === MetricType.TYPE_CRASH ? metricType : undefined,
      showTime: isBuglyCaseDetailPage || isBuglyListPage,
      timeQuickOptions: isBuglyListPage
        ? undefined : getTimeQuickOptions(props.issueType, isBuglyCaseDetailPage || isBuglyListPage),
      hiddenTimeWindow: !isBuglyListPage,
      isIssuesTimeWindow: isBuglyListPage,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      initTimeWindow: getDefaultTime.timeWindow,
      initRange: getDefaultTime.initRange,
      defaultTimeQuick: getDefaultTime.timeQuick,
      timeTypeOptions: TIME_TYPE_OPTIONS_BUGLY,
      getTimeQuickOptions: isBuglyListPage
        ? timeWindow => (getTableTimeQuickOptions(
          props.issueType,
          isBuglyCaseDetailPage || isBuglyListPage, timeWindow,
        )) : undefined,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAppVersion,
      label: FIELD_LABEL[FieldKey.AppVersion],
      labelString: FIELD_LABEL[FieldKey.AppVersion],
      isSplit: true,
      fieldKey: FieldKey.AppVersion,
      fieldScope: getFieldZone(props.issueType),
      scope: 'issues',
      mode: 'tags',
      deleteable: false,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
        style: {
          width: 106,
        },
      },
      valueSelectProps: {
        mode: 'tags',
        style: {
          width: 450,
        },
        onBlur: isCesDisabled ? openXmMeterOpenCes : undefined,

      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAccountId,
      label: '用户ID',
      labelString: '用户ID',
      isSplit: true,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardQimei,
      label: '设备ID',
      labelString: '设备ID',
      isSplit: true,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardStack',
      label: FIELD_LABEL[FieldKey.Stack],
      isSplit: true,
      labelString: FIELD_LABEL[FieldKey.Stack],
      mode: 'tags',
      initialValue: {
        mode: MatchMode.TOKENIZATION,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.TOKENIZATION,
      },
      convertValue: tokenizeValue,
      modeOptions: STACK_CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.Select,
      name: 'standardAppInBack',
      label: hintText('前后台状态', enumFilterTips),
      labelString: '前后台状态',
      options: getLocalField(FieldKey.AppInBack)
        ?.map(val => ({ ...val, value: getAppInBack(val.value) })) as ExOption[],
      mode: 'multiple',
      initialValue: [getAppInBack(getDefault('appInBack'))],
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage),
    },
    {
      type: FormType.SelectNumberGroup,
      name: 'standardLaunchDuration',
      label: hintText('使用时长（秒）', numberFilterTips),
      labelString: '使用时长（秒）',
      options: NUMBER_MATCH_OPTIONS,
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: FieldsOperator.Lt,
          value: [value],
        };
      },
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage),
      compareDisabled: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: 'standardFreeMem',
      label: hintText('可用内存（GB）', numberFilterTips),
      labelString: '可用内存（GB）',
      options: NUMBER_MATCH_OPTIONS,
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: FieldsOperator.Lt,
          value: [value],
        };
      },
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage),
      compareDisabled: true,
    },
    {
      type: FormType.Select,
      name: 'exceptionTypeList',
      label: hintText('Issue 类型', enumFilterTips),
      labelString: 'Issue 类型',
      options: exceptionTypeList,
      mode: 'single',
      initialValue: exceptionTypeList?.[0]?.value,
      disabled: !isBuglyListPage
        || [IssueType.ISSUE_TYPE_ANR_BUGLY, IssueType.ISSUE_TYPE_FOOM_BUGLY].includes(props.issueType),
      compareDisabled: true,
    },
    {
      type: FormType.Select,
      name: 'deviceType',
      label: hintText('设备类型', enumFilterTips),
      labelString: '设备类型',
      options: TABLE_DEVICE_TYPE_OPTIONS,
      mode: 'multiple',
      initialValue: [TABLE_DEVICE_TYPE_OPTIONS?.[0]?.value],
      disabled: !showDeviceType,
      compareDisabled: true,
    },
    {
      type: FormType.Select,
      name: 'platform',
      label: hintText('平台', enumFilterTips),
      labelString: '平台',
      options: getLocalField(FieldKey.Platform) as ExOption[],
      mode: 'single',
      initialValue: FIELD_ALL,
      disabled: !(isErrorList && isFlutter()),
      compareDisabled: !(isErrorList && isFlutter()),
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardBundleId,
      label: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      isSplit: true,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardChannelId',
      label: '渠道',
      isSplit: true,
      fieldKey: FieldKey.ChannelId,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.Is,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardStage',
      label: FIELD_LABEL[FieldKey.Stage],
      isSplit: true,
      fieldKey: FieldKey.Stage,
      fieldScope: getFieldZone(props.issueType),
      dropdownMatchSelectWidth: 500,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardBrand,
      label: FIELD_LABEL[FieldKey.Brand],
      isSplit: true,
      disabled: !isAndroid(),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardShiplyTag,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.Exception,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      disabled: !config.enableShiplyAbTestTag,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAbTestTag,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.Exception,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardTagsInFilter,
      label: hintText(FIELD_LABEL[FieldKey.TagsInFilter], stringArrayFilterTips),
      labelString: FIELD_LABEL[FieldKey.TagsInFilter],
      fieldScope: getMonitorType(metricType),
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      compareDisabled: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardInferredLabels,
      label: hintText('数据标记', DATA_LABELING_TIPS),
      labelString: '数据标记',
      mode: 'tags',
      options: inferredLabelsConfig?.labelOptions || [],
      initialValue: {
        mode: MatchMode.NOT,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.NOT,
      },
      modeOptions: INFERRED_LABELS_OPTIONS,
      compareDisabled: true,
      disabled: !isDataLabelingPlatform(),
    },
    {
      type: FormType.CharacterInput,
      name: 'standardCpuArch',
      label: isApple ? hintText('CPU架构', cpuArchitectureText) : hintText('运行时架构', cpuArchText),
      labelString: isApple ? 'CPU架构' : '运行时架构',
      fieldScope: getFieldZone(props.issueType),
      fieldKey: FieldKey.CpuArc,
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardCpuArchitecture',
      label: hintText('CPU架构', cpuArchitectureText),
      labelString: 'CPU架构',
      fieldKey: FieldKey.CpuArchitecture,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      disabled: isApple,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardSdkVersion,
      label: hintText(FIELD_LABEL[FieldKey.SdkVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
      dropdownMatchSelectWidth: 300,
      rangeProps: {
        dropDownWidth: 220,
      },
    },
    {
      type: FormType.CharacterInput,
      name: 'standardIssueId',
      label: 'Issue ID',
      labelString: 'Issue ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: isBuglyDetailPage,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardCrashId',
      label: '消息ID',
      labelString: '消息ID',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardErrorType',
      label: '异常名称',
      labelString: '异常名称',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: (props.issueType === IssueType.ISSUE_TYPE_FOOM_BUGLY) || isBuglyDetailPage,
      compareDisabled: true,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardHardware',
      label: '机型',
      labelString: '机型',
      mode: 'tags',
      fieldKey: FieldKey.Hardware,
      fieldScope: getFieldZone(props.issueType),
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardOsVersion,
      label: hintText(FIELD_LABEL[FieldKey.OsVersion], versionFilterTips),
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldKey: FieldKey.OsVersion,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardProcessName',
      label: '出错进程',
      labelString: '出错进程',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardThreadName',
      label: '出错线程',
      labelString: '出错线程',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardMainStack',
      label: 'ANR Trace main线程堆栈',
      labelString: 'ANR Trace main线程堆栈',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      disabled: !mainStackEnabled || ![FilterEntry.ANRList, FilterEntry.ANRDetail].includes(entry),
      compareDisabled: true,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardRom',
      disabled: !(isBuglyListPage || isBuglyCaseDetailPage),
      label: 'ROM',
      labelString: 'ROM',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
    },
    {
      label: hintText('异常现场', '搜索包含以下现场信息的个例，选择多个时，只要包含其中一个即表示满足条件。'),
      labelString: '异常现场',
      type: FormType.CharacterInput,
      name: FieldKey.StandardSceneDataLabels,
      fieldKey: FieldKey.Model,
      options: getSceneDataLabelsOptions(),
      mode: 'multiple',
      disabled: isBuglyListPage || !isBuglyCaseDetailPage || !isAndroid(),
      compareDisabled: isBuglyListPage || !isBuglyCaseDetailPage || !isAndroid(),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: SPECIFIC_TYPE,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardLinkages',
      labelString: '联动标记',
      label: hintText('联动标记', specialFilterTips),
      options: getLinkageOptions(),
      mode: 'tags',
      disabled: isLinkageDisabled || !isAndroid(),
      compareDisabled: true,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: SPECIFIC_TYPE,
    },
    {
      type: FormType.Select,
      name: FieldKey.Linkages,
      labelString: '联动标记',
      label: '联动标记',
      options: ANDROID_FOOM_LINKAGES_OPTION,
      mode: 'tags',
      disabled:
        ![FilterEntry.FoomList, FilterEntry.FoomDetail].includes(props.entry),
      compareDisabled: true,
    },
    {
      type: FormType.Select,
      name: 'oomType',
      label: hintText(FIELD_LABEL[FieldKey.OOMType], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.OOMType],
      fieldScope: Zone.AndroidFoom,
      fieldKey: FieldKey.OOMType,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      compareDisabled: true,
      disabled: props.issueType !== IssueType.ISSUE_TYPE_FOOM_BUGLY || isBuglyDetailPage,
    },
    {
      type: FormType.Select,
      name: FieldKey.StandardContinuousEvent,
      label: <ContinuousEventTips />,
      labelString: '连续崩溃',
      fieldKey: FieldKey.ContinuousEvent,
      fieldScope: Zone.Exception,
      disabled: ![FilterEntry.CrashDetail, FilterEntry.CrashList].includes(props.entry),
      compareDisabled: true,
      mode: 'single',
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
    },
    {
      type: FormType.BuglyCustomKeys,
      name: FieldKey.BizExtendInfo,
      label: '自定义字段',
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAttachFileNames,
      labelString: '附件',
      label: hintText(FIELD_LABEL[FieldKey.AttachFileNames], attachFileNamesText),
      options: isIOS() || isMac() || isVisionOS()
        ? IOS_ATTACH_FILE_OPTIONS : ANDROID_ATTACH_FILE_OPTIONS,
      mode: 'tags',
      disabled: isBuglyListPage || !isBuglyCaseDetailPage,
      compareDisabled: true,
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: SPECIFIC_TYPE,
    },
    {
      type: FormType.CharacterInput,
      name: 'standardBuildNumber',
      label: '构建号',
      labelString: '构建号',
      mode: 'tags',
      initialValue: {
        mode: MatchMode.MATCH,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.MATCH,
      },
      isSplit: true,
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardAsanType,
      label: hintText(FIELD_LABEL[FieldKey.AsanType], asanTypeTips),
      labelString: FIELD_LABEL[FieldKey.AsanType],
      options: getAsanOptions(),
      fieldKey: FieldKey.AsanType,
      fieldScope: getFieldZone(props.issueType),
      mode: 'tags',
      disabled: !isAndroid() || (entry !== FilterEntry.CrashList && entry !== FilterEntry.CrashDetail),
      initialValue: {
        mode: MatchMode.OR,
        values: undefined,
      },
      modeSelectProps: {
        defaultValue: MatchMode.OR,
      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
  ];

  useEffect(() => {
    const { initialValue } = props;
    if (initialValue) {
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, initialValue)?.then?.(() => {
        form.setFieldsValue(initialValue);
        if (Array.isArray(initialValue.timeRange) && initialValue.timeRange.length === 2) {
          judgmentTime(
            moment(initialValue.timeRange[0]),
            moment(initialValue.timeRange[1]),
            isBuglyListPage ? initialValue.timeWindow : undefined,
            false,
            true,
            judgmentTimeCallback,
          );
        }
      });
    }
  }, [props.initialValue]);

  useEffect(() => {
    const token = getParamFromUrl('token');
    if (!token) {
      form.submit();
    }
  }, []);

  return <QueryForm
    formProps={{
      isKeyPressSubmit: false,
    }}
    ref={tableRef}
    form={form}
    entries={entries}
    onFinish={onFinish}
    pageEntry={props.entry}
    onDeletedKeys={props.onDeletedKeys}
    defaultValues={props.defaultValues}
    hasCompare={isBuglyListPage}
    issueType={props.issueType}
    cachePrefix={'prefix-8'}
    isSaveSearchValues={isBuglyListPage}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
  />;
});
